import { FC, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/libs/utils";
import Link from "next/link";
import { useTrans } from "src/utils/useTrans";
import Image from "next/image";
import { NewsItem } from "src/models/News";
import Paginate from "./paginate";

type Props = {
  className?: string;
  list: NewsItem[];
};

const DisplayNews: FC<Props> = ({ className, list }) => {
  const { t } = useTrans(["support"]);
  const ref = useRef<any>(null);

  const [state, setState] = useState({
    viewsPerPage: 8,
    pages: 0,
    currentPage: 1,
  });

  useEffect(() => {
    const pages = Math.ceil(list.length / state.viewsPerPage);
    setState((state: any) => ({ ...state, pages, currentPage: 1 }));
  }, [list, state.viewsPerPage]);

  const handleUpdate = (to: number) => {
    setState((state: any) => ({ ...state, currentPage: to }));
  };
  let childNodes: any = null;
  if (ref.current) {
    childNodes = ref.current.childNodes;
  }
  return (
    <div className={cn(className)}>
      <div
        ref={ref}
        className={cn(
          "grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 sm:gap-y-12",
        )}
      >
        {list.map((item, index) => {
          let content: any = null;
          if (!!childNodes && childNodes[index]) {
            const target = [...childNodes[index].childNodes].filter(
              (child) => child.nodeName === "P",
            )[0];
            content = {};
            content.height = 72;
            content.actualHeight = target?.scrollHeight ?? 0;
          }
          const from = (state.currentPage - 1) * state.viewsPerPage;
          const to = state.currentPage * state.viewsPerPage - 1;
          const within = index >= from && index <= to;
          const internal = (item?.link ?? "").startsWith("/support/news/");
          const parsed = new Date(!item.date ? 0 : item.date);
          const date = isNaN(parsed.getTime())
            ? ""
            : parsed.toISOString().slice(0, 10);

          return (
            <Link
              target={internal ? "_self" : "_blank"}
              key={index}
              href={item?.link ?? ""}
              className={cn({ hidden: !within })}
            >
              <div
                className={cn(
                  "group flex flex-col",
                  "hover:[&>.title]:underline",
                  "hover:[&_img:first-of-type]:scale-[1.15]",
                )}
              >
                <span className={"text-sm font-700 uppercase"}>
                  {t(`${(item.category ?? "").toLowerCase()}`)}
                </span>
                <NewsThumbnail
                  src={item.thumbnailSrc}
                  alt={item.title || "news thumbnail"}
                />
                <span
                  className={
                    "title mt-5 flex flex-grow whitespace-normal break-all text-sm font-700 sm:text-base"
                  }
                >
                  {item.title}
                </span>
                <p
                  className={
                    "word relative my-2.5 h-[4.5rem] overflow-hidden overflow-ellipsis break-all leading-6"
                  }
                >
                  {item.summary}
                  {!!content && content.actualHeight > content.height && (
                    <span
                      className={
                        "absolute bottom-0 flex h-6 w-full items-center justify-end from-transparent to-white"
                      }
                    >
                      <span className={"leading-6 opacity-50"}>...</span>
                    </span>
                  )}
                </p>
                <span className={"text-xs"}>{date}</span>
              </div>
            </Link>
          );
        })}
      </div>
      <Paginate
        currentPage={state.currentPage}
        pageCount={Math.ceil(list.length / state.viewsPerPage)}
        onUpdate={handleUpdate}
      />
    </div>
  );
};

const FALLBACK_SRC = "/images/fallback.png";

const NewsThumbnail: FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  // src 를 state 에 얼리면 재정렬/필터로 같은 슬롯에 다른 기사가 와도 썸네일이 안 바뀐다.
  // src 는 prop 에서 직접 읽고, 로드 실패 여부만 추적하되 src 가 바뀌면 리셋한다.
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const imgSrc = errored ? FALLBACK_SRC : src;
  const hasValidSrc = imgSrc?.startsWith("/") || imgSrc?.startsWith("http");

  const handleError = useCallback(() => {
    setErrored(true);
  }, []);

  return (
    <div className={"relative mt-2.5 block h-52 overflow-hidden bg-gray-100"}>
      {hasValidSrc && (
        <Image
          className={
            "h-full w-full object-cover transition duration-500 ease-in-out"
          }
          src={imgSrc!}
          alt={alt}
          fill
          onError={handleError}
        />
      )}
    </div>
  );
};

export default DisplayNews;
